import uuid from "uuid";
import {WizardGroup, WizardItem} from "../../../../../interfaces/Wizard";
import store from "../../../../../store";

/**
 * Decide which groups are "valid" (visible/allowed) besides enabled/disabled.
 * NOTE: this is your existing logic, pulled into a function.
 */
function isGroupAllowed({ wizGroup, wizardItem, currentVisType }) {
  if (wizGroup.hideForCustomers && !document.kvConfig.isBackend) return false;

  if (wizardItem.visualizer === "all") {
    const showWizardGroup = wizGroup.groupName === "Room" ||
      !Array.isArray(wizGroup.visibleInVisualizers) ||
      !wizGroup.visibleInVisualizers.length ||
      wizGroup.visibleInVisualizers.includes(currentVisType);
    if (showWizardGroup) {
      return true;
    }
    const fabricatorSubscriptions = store.getState().fabricatorState.fabricatorSubscriptions;
    // here we check if we don't have anymore active subscription, but the group is still hidden because of it
    return wizGroup.visibleInVisualizers.every((visType) => {
      return !fabricatorSubscriptions.some(sub => sub.service.toLowerCase() === visType)
    })
  }

  return true;
}

function buildAdj(edges) {
  const out = new Map(); // source -> [edge]
  const inc = new Map(); // target -> [edge]
  for (const e of edges) {
    if (!out.has(e.source)) out.set(e.source, []);
    if (!inc.has(e.target)) inc.set(e.target, []);
    out.get(e.source).push(e);
    inc.get(e.target).push(e);
  }
  return { out, inc };
}

function edgeKey(source, target, sourceHandle = null, targetHandle = null) {
  return `${source}|${target}|${sourceHandle ?? ""}|${targetHandle ?? ""}`;
}

function addEdgeUnique(edges, seen, edge) {
  const k = edgeKey(edge.source, edge.target, edge.sourceHandle, edge.targetHandle);
  if (seen.has(k)) return;
  seen.add(k);
  edges.push(edge);
}

/**
 * Main graph transform:
 * - removes groups that are "not allowed" (hideForCustomers/visualizer rules)
 * - handles disabled groups with your sibling/bypass logic
 * - removes unreachable groups/edges (from startId)
 */
export function pruneWizardItemGraph(wizardItem: WizardItem, currentVisType) {
  const startId = "0";
  const groupsById = new Map<string, WizardGroup>(wizardItem.wizardGroups.map((g) => [String(g.id), g]));
  let edges = wizardItem.wizardEdges.map((e) => ({
    ...e,
    source: String(e.source),
    target: String(e.target),
  }));

  // 1) Hard-remove groups that are not allowed by non-enabled rules (eyeBall/visualizer etc.)
  const hardRemoved = new Set();
  for (const g of wizardItem.wizardGroups) {
    if (!isGroupAllowed({ wizGroup: g, wizardItem, currentVisType })) {
      hardRemoved.add(String(g.id));
    }
  }

  // Remove edges touching hard-removed nodes (no bypass here by default)
  edges = edges.filter((e) => !hardRemoved.has(e.source) && !hardRemoved.has(e.target));

  // 2) Disabled handling with "remove vs bypass" rule
  // We'll look at "valid children" of each parent (children that are NOT hardRemoved).
  // The sibling rule uses presence of another valid child besides the disabled node.
  const isDisabled = (id) => {
    const g = groupsById.get(id);
    return !!g && g.enabled === false;
  };

  const disabledIds = new Set<string>(
    wizardItem.wizardGroups
      .map((g) => String(g.id))
      .filter((id) => !hardRemoved.has(id) && isDisabled(id))
  );

  // We'll iteratively process because bypassing changes edges.
  // In practice, one pass is often enough, but this makes it safer for chains of disabled nodes.
  for (let guard = 0; guard < 50; guard++) {
    const { out, inc } = buildAdj(edges);

    // Helper: count "valid siblings" for a given (parent -> child) relationship
    const parentHasOtherValidChild = (parentId, childId) => {
      const outs = out.get(parentId) || [];
      // other child means: different target, not hardRemoved
      // (we also consider disabled children as "valid child" for sibling check? usually yes,
      // but if you want only enabled siblings, change the condition below)
      return outs.some((e) => e.target !== childId && !hardRemoved.has(e.target));
    };

    let changed = false;
    const toBypass = new Set<string>();
    const toRemoveNoBypass = new Set<string>();

    for (const id of disabledIds) {
      // if it already disappeared from edges entirely, we'll handle later via reachability
      const incoming = inc.get(id) || [];
      if (!incoming.length) {
        // still can be bypassed if it's in the middle of a chain and has outgoing,
        // but if no incoming, bypassing won't help; treat as removable
        toRemoveNoBypass.add(id);
        continue;
      }

      // If ANY parent has another valid child, we remove this node (no bypass)
      const hasSiblingFromSomeParent = incoming.some((inE) =>
        parentHasOtherValidChild(inE.source, id)
      );

      if (hasSiblingFromSomeParent) {
        toRemoveNoBypass.add(id);
      } else {
        toBypass.add(id);
      }
    }

    // Apply removals (no bypass): just drop edges touching the node
    if (toRemoveNoBypass.size) {
      const before = edges.length;
      edges = edges.filter((e) => !toRemoveNoBypass.has(e.source) && !toRemoveNoBypass.has(e.target));
      if (edges.length !== before) changed = true;
      for (const id of toRemoveNoBypass) disabledIds.delete(id);
    }

    // Apply bypass:
    if (toBypass.size) {
      const { out: out2, inc: inc2 } = buildAdj(edges);

      const nextEdges = [];
      const seen = new Set();

      // seed with existing edges excluding those touching bypassed nodes
      for (const e of edges) {
        if (toBypass.has(e.source) || toBypass.has(e.target)) continue;
        addEdgeUnique(nextEdges, seen, e);
      }

      // For each bypass node X:
      // connect each parent P -> X with each X -> C into P -> C
      for (const x of toBypass) {
        const incoming = inc2.get(x) || [];
        const outgoing = out2.get(x) || [];

        for (const inE of incoming) {
          for (const outE of outgoing) {
            // Avoid self loops
            if (inE.source === outE.target) continue;

            addEdgeUnique(nextEdges, seen, {
              id: `reactflow__edge-${inE.source}-${outE.target}-${uuid()}`,
              source: inE.source,
              target: outE.target,
              // keep handles if you care; otherwise null is fine
              sourceHandle: inE.sourceHandle ?? null,
              targetHandle: outE.targetHandle ?? null,
            });
          }
        }
      }

      if (nextEdges.length !== edges.length) changed = true;
      edges = nextEdges;
      for (const id of toBypass) disabledIds.delete(id);
    }

    if (!changed) break;
  }

  // 3) Reachability cleanup:
  // keep only nodes reachable from startId through edges
  const { out: outFinal } = buildAdj(edges);
  const reachable = new Set([String(startId)]);
  const queue = [String(startId)];

  while (queue.length) {
    const cur = queue.shift();
    const outs = outFinal.get(cur) || [];
    for (const e of outs) {
      if (!reachable.has(e.target)) {
        reachable.add(e.target);
        queue.push(e.target);
      }
    }
  }

  // Filter groups: must be reachable AND not hardRemoved AND enabled (because disabled should be gone now)
  const wizardGroups = wizardItem.wizardGroups.filter((g) => {
    const id = String(g.id);
    if (hardRemoved.has(id)) return false;
    if (g.enabled === false) return false;
    return reachable.has(id);
  });

  // Filter edges: both ends must be reachable; and targets/sources must not be hardRemoved
  const wizardEdges = edges.filter((e) => {
    if (hardRemoved.has(e.source) || hardRemoved.has(e.target)) return false;
    return reachable.has(e.source) && reachable.has(e.target);
  });

  return {
    ...wizardItem,
    wizardGroups,
    wizardEdges,
  };
}
